<template>
  <div
    class="tw-flex tw-min-w-0 tw-flex-col tw-justify-center"
    data-testid="query-toolbar-header"
  >
    <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
      <!-- 80px keeps a readable part of the name on a narrow window. The
           important marker overrides the shared label's own min-w-0. -->
      <SfTruncatedLabel
        class="
          !tw-min-w-20
          tw-max-w-[440px] tw-text-base tw-font-medium tw-text-sf-slate-darker-2
        "
        :text="queryName"
        data-testid="query-toolbar-name"
      />

      <span
        v-if="showRecommendedBadge"
        class="sf-label tw-inline-flex tw-shrink-0 tw-items-center"
        data-testid="query-toolbar-recommended"
      >
        <i
          class="
            fa fa-solid fa-circle-dot
            tw-mr-1 tw-text-[8px] tw-leading-none tw-text-sf-slate
          "
        />
        {{ I18n.t('companies_overview.segments_new.recommended') }}
      </span>

      <div
        v-if="segment"
        class="
          sf-query-toolbar-indicators
          tw-flex tw-shrink-0 tw-items-center tw-gap-1
        "
        :class="{ 'sf-query-toolbar-indicators--open': dropdownOpen }"
        data-testid="query-toolbar-indicators"
      >
        <button
          v-tooltip.placementBottom="pinTooltip"
          type="button"
          class="
            sf-button sf-button--tertiary sf-button--lg sf-button--icon-only
          "
          :disabled="pinPending"
          :aria-label="pinTooltip"
          data-testid="query-toolbar-pin-btn"
          @click="onTogglePin"
        >
          <FontAwesomeIcon
            :icon="['fas', pinned ? 'thumbtack' : 'thumbtack-slash']"
          />
        </button>

        <button
          v-if="visibility"
          v-tooltip.placementBottom="visibility.desc"
          type="button"
          class="
            sf-button sf-button--tertiary sf-button--lg sf-button--icon-only
          "
          :aria-label="visibility.desc"
          data-testid="query-toolbar-visibility-btn"
          @click="onVisibilityClick"
        >
          <img :src="$imagesRequire(visibility.path)" alt="" />
        </button>

        <SfDropdown
          :dropdown-id="dropdownId"
          :is-open="dropdownOpen"
          variant="seamless"
          unstyled-trigger
          @dropdown-clicked="toggleDropdown"
          @dropdown-closed="closeDropdown"
        >
          <template #trigger>
            <SfButtonDs
              variant="tertiary"
              size="lg"
              icon-only
              icon-before="ellipsis-h"
              :aria-label="I18n.t('companies_overview.toolbar.options')"
              data-testid="query-toolbar-options-btn"
            />
          </template>
          <template #menu>
            <SfDropdownItem
              v-if="canEdit"
              data-testid="query-toolbar-menu-edit"
              @click="onEdit"
            >
              {{ I18n.t('companies_overview.toolbar.edit_query_details') }}
            </SfDropdownItem>
            <SfDropdownItem
              data-testid="query-toolbar-menu-copy"
              @click="onMakeCopy"
            >
              {{ I18n.t('companies_overview.segments_new.make_a_copy') }}
            </SfDropdownItem>
            <SfDropdownItem
              v-if="canDestroy"
              class="!tw-text-sf-danger"
              data-testid="query-toolbar-menu-delete"
              @click="onDelete"
            >
              {{ I18n.t('companies_overview.toolbar.delete_query') }}
            </SfDropdownItem>
          </template>
        </SfDropdown>
      </div>
    </div>

    <SfTruncatedLabel
      v-if="segment?.description"
      class="!tw-min-w-20 tw-max-w-[600px] tw-text-sm tw-text-sf-slate-light-2"
      :text="segment.description"
      data-testid="query-toolbar-description"
    />
  </div>
</template>

<script lang="ts">
// A plain <script> block runs once at module load, not once per instance.
// thumbtack-slash ships only with the SVG icon set, not the app's Font Awesome
// webfont, so both pin glyphs are rendered via FontAwesomeIcon.
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faThumbtack,
  faThumbtackSlash,
} from '@fortawesome/free-solid-svg-icons'

library.add(faThumbtack, faThumbtackSlash)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import {
  SfDropdown,
  SfDropdownItem,
} from '@app/shared/vue/components/sf-dropdown'
import { SfButtonDs } from '@silverfin/ui-components'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useSegmentsOverviewStore } from '@app/insights/segments-overview/store'
import { unsavedQueryLabel } from '@app/insights/segments-overview/all-clients-query'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import useSegmentVisibilities from '@app/insights/composables/use-segment-visibilities'
import { openDeleteSegmentConfirmation } from '@app/insights/utils/delete-segment-confirmation'

defineOptions({ name: 'InsightsQueryToolbarHeader' })

const I18n = useI18n()
const segmentsStore = useSegmentsStore()
const overviewStore = useSegmentsOverviewStore()
const rootStore = useInsightsRootStore()
const dropdownStore = useDropdown()
const visibilities = useSegmentVisibilities()

const { segment, isSegmentLoading } = storeToRefs(segmentsStore)
const { currentDropdownId } = storeToRefs(dropdownStore)

const dropdownId = 'query-toolbar-options'
const dropdownOpen = computed(() => currentDropdownId.value === dropdownId)

// loadSegment publishes the id-only payload before the fetch resolves, so the
// name is absent for the length of the request. Stay blank until it lands
// rather than claiming the query is "All clients".
const queryName = computed(() => {
  if (segment.value?.name) return segment.value.name
  if (isSegmentLoading.value || segment.value) return ''

  return unsavedQueryLabel(rootStore.newQuery)
})
const showRecommendedBadge = computed(
  () => segment.value?.publisher === 'silverfin' && !!segment.value?.recommended
)
const visibility = computed(() =>
  segment.value ? visibilities[segment.value.visibility] : null
)
const pinned = computed(
  () => !!segment.value && overviewStore.isPinned(segment.value)
)
const pinPending = computed(
  () => !!segment.value && overviewStore.isPinPending(segment.value)
)
const pinTooltip = computed(() =>
  pinned.value
    ? I18n.t('companies_overview.toolbar.unpin_query')
    : I18n.t('companies_overview.toolbar.pin_query')
)
const canEdit = computed(() => !!segment.value?.permissions?.update)
const canDestroy = computed(() => !!segment.value?.permissions?.destroy)

function toggleDropdown(): void {
  dropdownStore.toggleDropdown(dropdownId)
}

function closeDropdown(): void {
  dropdownStore.closeDropdown()
}

async function onTogglePin(): Promise<void> {
  if (!segment.value) return
  await overviewStore.togglePin(segment.value)
}

function onEdit(): void {
  // The menu's Edit item is v-if-gated on canEdit; the guard backs that up.
  if (!segment.value || !canEdit.value) return
  closeDropdown()
  segmentsStore.openEditSegmentModal(segment.value)
}

// Opens the modal regardless of permissions, like Insights 1.0 — without
// update permission it renders read-only (with a Make a copy button).
function onVisibilityClick(): void {
  if (!segment.value) return
  segmentsStore.openEditSegmentModal(segment.value)
}

function onMakeCopy(): void {
  closeDropdown()
  segmentsStore.openSaveAsSegmentModal()
}

function onDelete(): void {
  if (!segment.value) return
  closeDropdown()
  openDeleteSegmentConfirmation(segment.value)
}
</script>

<style lang="scss">
/* The reveal target is the whole subheader row, which spans two separately
   mounted apps - only an ancestor selector can express that. Every rule is
   scoped under #subheader so that, mounted anywhere else, the controls stay
   visible instead of being stuck at opacity 0. Hiding with opacity rather
   than `display: none` keeps the buttons in the tab order, so `:focus-within`
   can reveal them for keyboard users. */
#subheader .sf-query-toolbar-indicators {
  opacity: 0;
  transition: opacity 0.1s ease-in;
}

#subheader .sf-query-toolbar-indicators--open,
#subheader .sf-query-toolbar-indicators:focus-within,
#subheader:hover .sf-query-toolbar-indicators {
  opacity: 1;
}

/* Keep the controls visible wherever hover is unreliable: pure touch devices
   (hover: none) and hybrids whose touchscreen never hovers (any-pointer:
   coarse). */
@media (hover: none), (any-pointer: coarse) {
  #subheader .sf-query-toolbar-indicators {
    opacity: 1;
  }
}
</style>
